import { z } from 'zod';
import { privacyCaptureSchema } from './privacy.js';
import { STATE_CODES } from '../india.js';
import { FOOD_TYPES } from '../menu-defaults.js';
import { SUBSCRIPTION_STATUSES } from '../platform.js';
import { optionalGstin, phone, pincode, rupees, shortText } from './common.js';
import { changePasswordSchema } from './identity.js';
/**
 * The business profile a hotelier fills in when they register.
 *
 * Every field here is mandatory except GSTIN: a property below the GST
 * registration threshold, or one that simply hasn't registered yet, can sign
 * up without one and add it later from Settings - see updatePropertySchema.
 * Everything else here appears on a tax invoice regardless of GST
 * registration, and a property that cannot raise a compliant invoice at all
 * is not usable.
 */
export const businessProfileSchema = z.object({
    name: shortText,
    legalName: shortText.optional(),
    email: z.string().trim().toLowerCase().email('Enter the hotel’s email address'),
    phone,
    gstin: optionalGstin,
    /** Chosen from the GST state list, not typed: a wrong code means wrong tax. */
    stateCode: z
        .string()
        .refine((code) => STATE_CODES.includes(code), 'Choose the state from the list'),
    address: z.string().trim().min(5, 'Enter the full address').max(240),
    city: shortText,
    /** Printed on every invoice, and required by the e-invoice schema. */
    pincode,
    /** A hotel serving food needs one, and it is printed on the invoice. */
    fssai: z
        .string()
        .trim()
        .regex(/^\d{14}$/, 'An FSSAI licence number is 14 digits'),
    /** Decides which starter menu the new hotel is given. */
    foodType: z.enum(FOOD_TYPES, {
        message: 'Say whether you serve vegetarian, non-vegetarian or both',
    }),
});
export const registrationOwnerSchema = z.object({
    name: shortText,
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    password: changePasswordSchema.shape.newPassword,
    phone: z.union([phone, z.literal('')]).optional(),
});
export const registerHotelSchema = z.object({
    business: businessProfileSchema,
    owner: registrationOwnerSchema,
    planCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9_-]{2,20}$/)
        .optional(),
    /**
     * The period the hotel picked while signing up.
     *
     * Recorded, not charged: registration still starts a trial and takes no
     * money. Keeping it means the billing page opens on the period they already
     * chose rather than asking again once the trial ends.
     */
    billingOptionId: z.string().uuid().optional(),
    /**
     * Whether they asked for the period above to renew on its own.
     *
     * Intent, not a mandate: nothing is authorised during registration, so this
     * only decides whether the first checkout after the trial opens a recurring
     * Cashfree subscription or a one-time order. Refused on the server for a
     * period that cannot recur, so a hand-edited request cannot record a promise
     * the gateway could never keep.
     */
    autoRenew: z.boolean().optional(),
    /** A promotional code (docs/platform/coupons.md). Validated, then redeemed
     *  only once the rest of registration actually succeeds - see
     *  registerHotel(). */
    couponCode: z.string().trim().toUpperCase().max(40).optional(),
    /**
     * Not optional, and not merely a boolean: only `true` parses.
     *
     * Registration is the moment the hotel enters a contract, so the agreement
     * has to be a positive act. What was agreed to, and when, is recorded on the
     * tenant - a tick box nobody keeps evidence of is theatre.
     */
    acceptTerms: z.literal(true, { error: 'Please accept the terms and conditions' }),
    /** Registration is a public write, so it carries a challenge like sign-in. */
    captchaId: z.string().uuid().optional(),
    captchaAnswer: z.string().trim().min(1).max(12).optional(),
    /** The reCAPTCHA token, when that is the challenge in use. */
    captchaToken: z.string().trim().min(1).max(4096).optional(),
    /** Emailed to the business address. Only required once SMTP is configured. */
    otp: z
        .string()
        .trim()
        .regex(/^\d{4,8}$/, 'Enter the code we emailed you')
        .optional(),
});
/* ---------------- plans ---------------- */
export const planCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{2,20}$/, 'Use 2-20 letters, digits, hyphen or underscore');
/**
 * Plan fields without defaults.
 *
 * The update schema is built from these rather than from `createPlanSchema`:
 * `.partial()` leaves a field's `.default()` in place, so a partial update that
 * only sent a price would also quietly reset visibility, the active flag, the
 * sort order and the feature list to their defaults.
 */
const planFields = {
    name: shortText,
    description: z.string().trim().max(240).optional().or(z.literal('')),
    priceMonthly: rupees,
    /** Replaces the rupee figure on the card when set, for a negotiated tier. */
    priceLabel: z.string().trim().max(24).optional().or(z.literal('')),
    trialDays: z.number().int().min(0).max(365),
    maxProperties: z.number().int().min(1).max(1000),
    maxUsers: z.number().int().min(1).max(10000),
    features: z.array(shortText).max(20),
    isPublic: z.boolean(),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(999),
};
export const createPlanSchema = z.object({
    code: planCode,
    ...planFields,
    features: z.array(shortText).max(20).default([]),
    isPublic: z.boolean().default(true),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const updatePlanSchema = z.object(planFields).partial();
/**
 * Deleting a plan that hotels are on needs somewhere to put them.
 *
 * Optional here because a plan nobody is on needs no destination; the server
 * is what refuses a deletion that would strand somebody, since only it knows
 * how many subscriptions are actually pointing at the plan.
 */
export const deletePlanQuerySchema = z.object({
    reassignTo: z.string().uuid().optional(),
});
/* ---------------- subscription administration ---------------- */
export const SUBSCRIPTION_ACTIONS = [
    'CHANGE_PLAN',
    'EXTEND_TRIAL',
    /** Adds months to a paying hotel, from whatever it already has. */
    'EXTEND_PLAN',
    'ACTIVATE',
    'SUSPEND',
    'CANCEL',
];
export const subscriptionActionSchema = z
    .object({
    action: z.enum(SUBSCRIPTION_ACTIONS),
    planCode: planCode.optional(),
    days: z.number().int().min(1).max(365).optional(),
    months: z.number().int().min(1).max(36).optional(),
    note: z.string().trim().max(240).optional().or(z.literal('')),
})
    .refine((v) => v.action !== 'CHANGE_PLAN' || Boolean(v.planCode), {
    message: 'Choose the plan to move this hotel onto',
    path: ['planCode'],
})
    .refine((v) => v.action !== 'EXTEND_PLAN' || typeof v.months === 'number', {
    message: 'Say how many months to add',
    path: ['months'],
})
    .refine((v) => v.action !== 'EXTEND_TRIAL' || typeof v.days === 'number', {
    message: 'Say how many days to add',
    path: ['days'],
});
/* ---------------- coupons ---------------- */
export const COUPON_TYPES = ['FREE_SUBSCRIPTION', 'TRIAL_EXTENSION'];
export const COUPON_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'CANCELLED'];
/**
 * Coupon fields without defaults - same split as `planFields` above, and for
 * the same reason: `updateCouponSchema` is built from this, not from
 * `.partial()` on the create schema, so a partial PATCH cannot quietly reset
 * `usageLimit`/`firstTimeCustomerOnly` to their defaults.
 */
const couponFields = {
    name: shortText.optional().or(z.literal('')),
    campaignId: z.string().uuid().optional(),
    type: z.enum(COUPON_TYPES),
    status: z.enum(COUPON_STATUSES),
    /** FREE_SUBSCRIPTION only. */
    freeMonths: z.number().int().min(1).max(24).optional(),
    /** TRIAL_EXTENSION only. */
    trialExtensionDays: z.number().int().min(1).max(365).optional(),
    /** Empty = usable with any plan. */
    applicablePlanCodes: z.array(planCode).max(20),
    usageLimit: z.number().int().min(1).max(1_000_000),
    validFrom: z.string().datetime().optional(),
    validUntil: z.string().datetime().optional(),
    firstTimeCustomerOnly: z.boolean(),
};
const couponTypeRefinements = (schema) => schema
    .refine((v) => v.type !== 'FREE_SUBSCRIPTION' || typeof v.freeMonths === 'number', {
    message: 'Say how many months are free',
    path: ['freeMonths'],
})
    .refine((v) => v.type !== 'TRIAL_EXTENSION' || typeof v.trialExtensionDays === 'number', {
    message: 'Say how many days to add',
    path: ['trialExtensionDays'],
});
export const createCouponSchema = couponTypeRefinements(z.object({
    /** Left blank to auto-generate one (coupons/codeGenerator.ts). */
    code: z.string().trim().toUpperCase().max(40).optional(),
    ...couponFields,
    applicablePlanCodes: z.array(planCode).max(20).default([]),
    usageLimit: z.number().int().min(1).max(1_000_000).default(1),
    status: z.enum(COUPON_STATUSES).default('ACTIVE'),
    firstTimeCustomerOnly: z.boolean().default(true),
}));
export const updateCouponSchema = z.object(couponFields).partial();
export const couponStatusActionSchema = z.object({
    status: z.enum(['ACTIVE', 'PAUSED', 'CANCELLED']),
});
export const generateCouponsSchema = couponTypeRefinements(z.object({
    campaignId: z.string().uuid().optional(),
    /** Every generated code in the batch is `PREFIX-XXXXXX`. */
    prefix: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9]{2,20}$/, 'Use 2-20 letters/digits'),
    /** Synchronous generation, capped - see docs/platform/coupons.md. */
    quantity: z.number().int().min(1).max(2000),
    name: shortText.optional().or(z.literal('')),
    type: z.enum(COUPON_TYPES),
    freeMonths: z.number().int().min(1).max(24).optional(),
    trialExtensionDays: z.number().int().min(1).max(365).optional(),
    applicablePlanCodes: z.array(planCode).max(20).default([]),
    usageLimit: z.number().int().min(1).max(1_000_000).default(1),
    validFrom: z.string().datetime().optional(),
    validUntil: z.string().datetime().optional(),
    firstTimeCustomerOnly: z.boolean().default(true),
}));
export const couponListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    status: z.enum(COUPON_STATUSES).optional(),
    type: z.enum(COUPON_TYPES).optional(),
    campaignId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Public, registration-time check - no auth, so deliberately minimal. */
export const validateCouponSchema = z.object({
    code: z.string().trim().toUpperCase().max(40),
    planCode: planCode.optional(),
});
/* ---------------- coupon campaigns ---------------- */
const campaignFields = {
    name: shortText,
    description: z.string().trim().max(500).optional().or(z.literal('')),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    isActive: z.boolean(),
};
export const createCampaignSchema = z.object({
    ...campaignFields,
    isActive: z.boolean().default(true),
});
export const updateCampaignSchema = z.object(campaignFields).partial();
/* ---------------- superadmin support for a hotel's users ---------------- */
/** Like resetPasswordSchema, but the user id is already in the URL - see
 *  PATCH/POST /platform/tenants/:tenantId/users/:userId[/...]. */
export const resetTenantUserPasswordSchema = z.object({
    newPassword: changePasswordSchema.shape.newPassword,
});
/* ---------------- platform queries ---------------- */
export const tenantListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    status: z.enum(SUBSCRIPTION_STATUSES).optional(),
    planCode: planCode.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const platformReportQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(365).default(30),
});
/* ---------------- outgoing mail ---------------- */
/* ---------------- sign-in challenge ---------------- */
/**
 * Google reCAPTCHA keys, set by a superadmin.
 *
 * A blank secret means "keep the one already saved" - the screen is never sent
 * the secret, so it has nothing to send back.
 */
export const recaptchaSettingsSchema = z.object({
    siteKey: z.string().trim().max(200),
    secretKey: z.string().trim().max(200).optional(),
    isEnabled: z.boolean(),
});
export const smtpSettingsSchema = z.object({
    host: z.string().trim().max(200),
    port: z.number().int().min(1).max(65535),
    secure: z.boolean(),
    username: z.string().trim().max(200).optional().or(z.literal('')),
    /** Blank means keep the stored one: a screen never receives it to send back. */
    password: z.string().max(200).optional().or(z.literal('')),
    fromName: z.string().trim().max(80),
    fromEmail: z.string().trim().toLowerCase().email('Enter the address mail is sent from'),
    isEnabled: z.boolean(),
});
export const testMailSchema = z.object({
    to: z.string().trim().toLowerCase().email('Where should the test go?'),
});
/* ---------------- payment providers ---------------- */
export const PAYMENT_PROVIDER_KINDS = ['RAZORPAY', 'PAYU', 'CASHFREE', 'STRIPE'];
export const paymentProviderEnum = z.enum(PAYMENT_PROVIDER_KINDS);
export const paymentSettingSchema = z.object({
    keyId: z.string().trim().max(200).optional().or(z.literal('')),
    keySecret: z.string().max(400).optional().or(z.literal('')),
    isLive: z.boolean().optional(),
    isEnabled: z.boolean().optional(),
});
export const confirmPaymentSchema = z.object({
    reference: z.string().trim().min(8).max(60),
    providerRef: z.string().trim().min(1).max(120),
    orderId: z.string().trim().min(1).max(120),
    signature: z.string().trim().min(8).max(400),
});
/* ---------------- public room booking ---------------- */
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-27');
export const availabilityQuerySchema = z.object({ from: isoDay, to: isoDay });
export const createBookingSchema = z.object({
    roomTypeId: z.string().uuid(),
    from: isoDay,
    to: isoDay,
    guestName: shortText,
    guestPhone: phone,
    guestEmail: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(10).optional(),
    note: z.string().trim().max(240).optional().or(z.literal('')),
    privacy: privacyCaptureSchema,
});
/* ---------------- registration OTP ---------------- */
export const requestOtpSchema = z.object({
    email: z.string().trim().toLowerCase().email('Enter the hotel email address'),
    hotelName: z.string().trim().max(120).optional().or(z.literal('')),
});
/** Asked while the owner's sign-in email is being typed, before anything is submitted. */
export const checkOwnerEmailSchema = z.object({
    email: registrationOwnerSchema.shape.email,
});
/* ---------------- plan billing options ---------------- */
export const BILLING_INTERVAL_UNITS = ['MONTH', 'YEAR'];
/**
 * One purchasable period of a plan.
 *
 * `intervalCount` is capped at 12 because a period longer than a year is not
 * something the checkout or the mandate rails express sensibly - and 1, 3, 6
 * and 12 months cover monthly, quarterly, half-yearly and annual between them.
 */
const billingOptionFields = {
    intervalUnit: z.enum(BILLING_INTERVAL_UNITS),
    intervalCount: z.number().int().min(1).max(12),
    price: rupees,
    currency: z.string().trim().length(3).toUpperCase(),
    supportsOneTime: z.boolean(),
    supportsRecurring: z.boolean(),
    /** The Cashfree plan this period maps to. Required before recurring works. */
    cashfreePlanId: z.string().trim().max(120).optional().or(z.literal('')),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(999),
};
export const createBillingOptionSchema = z
    .object({
    ...billingOptionFields,
    currency: z.string().trim().length(3).toUpperCase().default('INR'),
    supportsOneTime: z.boolean().default(true),
    supportsRecurring: z.boolean().default(false),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
})
    .refine((value) => !value.supportsRecurring || Boolean(value.cashfreePlanId), {
    // Offering auto-renewal with nothing to renew against would fail after the
    // customer has already committed to paying.
    message: 'Map a Cashfree plan before enabling recurring for this period',
    path: ['cashfreePlanId'],
})
    .refine((value) => value.supportsOneTime || value.supportsRecurring, {
    message: 'A period has to be purchasable one way or the other',
    path: ['supportsOneTime'],
});
export const updateBillingOptionSchema = z.object(billingOptionFields).partial();
/* ---------------- billing configuration ---------------- */
/**
 * Who issues a subscription invoice, and at what tax rate.
 *
 * Held as configuration rather than constants because the operating company,
 * its GSTIN and the rate that applies are business facts that change without a
 * release - and because nothing in this codebase should assert a tax rate it
 * was not told.
 */
export const billingEntitySchema = z.object({
    legalName: z.string().trim().max(160),
    gstin: z.string().trim().max(15).optional().or(z.literal('')),
    address: z.string().trim().max(240).optional().or(z.literal('')),
    /// Two-digit GST state code of the seller, for place-of-supply.
    stateCode: z.string().trim().max(2).optional().or(z.literal('')),
    email: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    phone: z.string().trim().max(20).optional().or(z.literal('')),
    /** Percentage applied to the subscription price. 18 means 18%. */
    taxRate: z.number().min(0).max(100),
    /** What the tax line is called on the invoice, e.g. "GST". */
    taxLabel: z.string().trim().max(24),
    /**
     * The SAC code for the subscription, printed on every invoice.
     *
     * Set here rather than written into the code because it is a tax
     * classification of what this business sells, and getting it wrong is the
     * seller's problem with their own filing - not something to be guessed by
     * whoever wrote the billing module.
     */
    sacCode: z.string().trim().max(10).optional().or(z.literal('')),
    /** Prefix for subscription invoice numbers, e.g. "EUR/SUB". */
    invoicePrefix: z.string().trim().max(16),
    currency: z.string().trim().length(3).toUpperCase(),
});
/** Which set of Cashfree keys and endpoints to use. */
export const CASHFREE_ENVIRONMENTS = ['TEST', 'LIVE'];
/**
 * Cashfree credentials, kept in the database so moving from the sandbox to the
 * live account is a change somebody makes in the console, not a deployment.
 * The secret is encrypted at rest and never returned to a screen.
 */
export const cashfreeSettingsSchema = z.object({
    environment: z.enum(CASHFREE_ENVIRONMENTS),
    /** x-client-id */
    appId: z.string().trim().max(120),
    /** x-client-secret. Blank on save means "keep the stored one". */
    secretKey: z.string().trim().max(200).optional().or(z.literal('')),
    /** Used to verify webhook signatures; blank means "keep". */
    webhookSecret: z.string().trim().max(200).optional().or(z.literal('')),
    isEnabled: z.boolean(),
});
/* ---------------- testimonials ---------------- */
/**
 * Split out for the same reason as `planFields`: `.partial()` on a schema that
 * carries `.default()` would let an edit of just the quote silently reset the
 * rating, the active flag and the sort order.
 */
const testimonialFields = {
    quote: z.string().trim().min(1, 'A testimonial needs the quote itself').max(400),
    authorName: shortText,
    authorTitle: z.string().trim().max(80).optional().or(z.literal('')),
    rating: z.number().int().min(1).max(5),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(999),
};
/** A headline figure on the registration page. Split for the `.partial()` reason above. */
const siteStatFields = {
    icon: z.string().trim().min(1, 'Pick an icon').max(4),
    value: z.string().trim().min(1, 'A figure needs something to show').max(16),
    label: shortText,
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(999),
};
export const createSiteStatSchema = z.object({
    ...siteStatFields,
    icon: z.string().trim().min(1).max(4).default('◆'),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const updateSiteStatSchema = z.object(siteStatFields).partial();
export const createTestimonialSchema = z.object({
    ...testimonialFields,
    rating: z.number().int().min(1).max(5).default(5),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const updateTestimonialSchema = z.object(testimonialFields).partial();
//# sourceMappingURL=platform.js.map